import { useEffect, useRef, useState } from 'react';
import { Check, Copy, ArrowUpRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArchiveSeal } from '@/components/ArchiveSeal';

interface ContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The archive's display name. */
  title: string;
  /** The archive's profile picture (the seal artwork). */
  picture?: string;
  /** Short statement about the archive (the profile's bio, cleaned). */
  about: string;
  /** The archive's npub. */
  npub: string;
  /** Where the archive's profile can be opened in a Nostr client. */
  profileUrl: string;
}

/**
 * Contact + About, in one place. The archive is run by its Nostr identity, so
 * "contact" means: who we are, and the address to reach us at.
 */
export function ContactDialog({
  open,
  onOpenChange,
  title,
  picture,
  about,
  npub,
  profileUrl,
}: ContactDialogProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(npub);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable: the address is selectable text below */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90svh] overflow-y-auto rounded-sm border-foreground/40 bg-background sm:max-w-md">
        <DialogHeader className="items-center text-center sm:text-center">
          <ArchiveSeal picture={picture} name={title} size={80} borderWidth={3} className="mb-1" />
          <DialogTitle className="font-engraved text-sm font-semibold uppercase tracking-[0.4em]">
            Contact
          </DialogTitle>
          <DialogDescription className="font-display text-base italic text-foreground/70">
            About the archive, and how to reach it
          </DialogDescription>
        </DialogHeader>

        <section aria-labelledby="about-heading" className="border-t-[3px] border-foreground pt-4">
          <h3
            id="about-heading"
            className="font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/60"
          >
            About
          </h3>
          <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground/85">
            {about}
          </p>
          <p className="mt-3 font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground">
            Built for Preservation, Education &amp; Litigation
          </p>
          <p className="mt-1 font-display text-sm italic text-foreground/80">
            Managed by 9/11 victim family members &amp; survivors
          </p>
        </section>

        <section aria-labelledby="reach-heading" className="border-t border-foreground/25 pt-4">
          <h3
            id="reach-heading"
            className="font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-foreground/60"
          >
            Reach us on Nostr
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground/75">
            Open the profile in any Nostr client to follow, message or reply, or copy the address
            below.
          </p>

          <div className="mt-3 flex items-stretch gap-2">
            <code
              className="min-w-0 flex-1 select-all break-all rounded-sm border border-foreground/25 bg-card px-2.5 py-2 font-mono text-[11px] leading-snug text-foreground"
              aria-label="The archive's Nostr address"
            >
              {npub}
            </code>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={copy}
              aria-label={copied ? 'Address copied' : 'Copy address'}
              className="h-auto w-10 shrink-0 rounded-sm border-foreground/40"
            >
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
            </Button>
          </div>
          <p role="status" aria-live="polite" className="sr-only">
            {copied ? 'Address copied to clipboard' : ''}
          </p>

          <Button
            asChild
            className="mt-3 w-full rounded-sm font-engraved text-[11px] font-semibold uppercase tracking-[0.24em]"
          >
            <a href={profileUrl} target="_blank" rel="noopener noreferrer">
              Open profile
              <ArrowUpRight aria-hidden="true" />
            </a>
          </Button>
        </section>
      </DialogContent>
    </Dialog>
  );
}
